import { ChatMessages, Features } from "@storytell/cp-client";
import type { MessageErrorData } from "@storytell/cp-client/src/gen/chat/v3/chat_messages_system_pb";
import { richTextToProseMirror } from "@storytell/rich-text";
import { produce } from "solid-js/store";
import {
  type SetStreamingStore,
  type StreamingBlockId,
  type StreamingChatId,
  type StreamingMessage,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingOperationId,
} from "../streamingTypes";

export const processMessage = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  console.log("zzz processMessage", message);
};

/**
 * MESSAGE_START is the first event we receive for a message.
 */
export const processMessageStart = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
) => {
  if (message.data.case !== "messageStartData") {
    console.error(new Error(`Expected messageStartData, got ${message.data.case}`));
    return;
  }
  const {
    id,
    index,
    blockId,
    operationId,
    role,
    data: { value: data },
  } = message;
  const chatId = data.chatId as StreamingChatId;

  try {
    if (onChatIdCaptured) {
      onChatIdCaptured(chatId);
    }
  } catch (error) {
    console.error("Error on the callback for capturing chat ID", error);
  }

  const messageId = id as StreamingMessageId;

  try {
    if (onMessageStarted) {
      onMessageStarted(messageId);
    }
  } catch (error) {
    console.error("Error on the callback for capturing message start", error);
  }

  const streamingMessage: StreamingMessage = {
    id: messageId,
    index,
    role,
    chatId,
    operations: [],
    feature: data.feature,
    status: StreamingMessageStatus.GENERATING,
    aggregateMetrics: {
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      totalCredits: 0,
      ingressCredits: 0,
      egressCredits: 0,
      lastUpdated: new Date(0),
    },
    prompt: {
      messageId,
      text: data.promptText,
      richText: data.promptRichText ? richTextToProseMirror(data.promptRichText) : undefined,
      blockId: blockId as StreamingBlockId,
      operationId: operationId as StreamingOperationId,
      scope: data.scope,
    },
  };

  setStore(
    produce((store) => {
      // Insert the message at the correct position
      if (!store.chats[chatId]) {
        store.chats[chatId] = {
          id: chatId,
          messages: [],
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
            lastUpdated: new Date(0),
          },
        };
      }

      store.messages[messageId] = streamingMessage;

      // Order the messages by oldest first
      if (store.chats[chatId] && !store.chats[chatId].messages.includes(messageId)) {
        const newMessages = [...store.chats[chatId].messages, messageId];
        store.chats[chatId].messages = newMessages.sort(
          (a, b) => (store.messages[a]?.index || 0) - (store.messages[b]?.index || 0),
        );
      }
    }),
  );
};

export const processMessageStop = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  const { id } = message;
  const messageId = id as StreamingMessageId;

  setStore(
    produce((store) => {
      const message = store.messages[messageId];
      // TODO: @andi streaming - should we throw here?
      // if (!message) {
      //   throw new Error(`Message not found for stop: ${messageId}`);
      // }
      if (message) {
        message.status = StreamingMessageStatus.COMPLETED;
      }
    }),
  );
};

export const processMessageError = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  if (message.data.case !== "messageErrorData") {
    console.error(new Error(`Expected messageErrorData, got ${message.data.case}`));
    return;
  }

  const { id, index: _index } = message;
  const originalMessageId = id as StreamingMessageId;

  setStore(
    produce((store) => {
      const originalMessage = store.messages[originalMessageId];
      const chatId = originalMessage?.chatId;
      if (!chatId) {
        console.error(new Error(`Chat not found for message error: ${originalMessageId}`));
        return;
      }

      // Create a new operation and block that will be attached to the
      // original message so we do not render an extra empty message card.
      const operationId = `operation_${originalMessageId}_error` as StreamingOperationId;
      const blockId = `block_${originalMessageId}_error` as StreamingBlockId;

      const streamingOperation = {
        index: 0,
        isComplete: true,
        operationId,
        messageId: originalMessageId,
        blocks: [blockId],
        name: "Error",
        activity: "error",
        startedAt: undefined,
        role: ChatMessages.Role.SYSTEM,
        status: ChatMessages.OperationStatus.FAILED,
        completedAt: new Date(),
        task: undefined,
        progressMessage: undefined,
      };

      const data = message.data.value as MessageErrorData;
      const streamingBlock = {
        type: "block" as const,
        isComplete: true,
        blockId,
        operationId,
        messageId: originalMessageId,
        role: ChatMessages.Role.SYSTEM,
        activity: "error",
        feature: originalMessage?.feature ?? (Features.Feature.UNSPECIFIED as unknown as Features.Feature),
        messageType: ChatMessages.MessageType.INTERSTITIAL_USER_FACING,
        title: "An error occurred",
        deltas: [],
        finalContent: { type: "text" as const, text: data?.displayMessage || "An error occurred" },
        searchResults: undefined,
        webSearchResults: undefined,
        conceptResults: undefined,
        structuredResults: undefined,
        metrics: undefined,
      };

      // Create chat if missing
      if (!store.chats[chatId]) {
        store.chats[chatId] = {
          id: chatId,
          messages: [],
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
            lastUpdated: new Date(0),
          },
        };
      }

      // Persist new entities (attached to the existing message)
      store.operations[operationId] = streamingOperation;
      store.blocks[blockId] = streamingBlock;

      // Attach the new operation to the original message if not already attached
      if (originalMessage) {
        if (!originalMessage.operations.includes(operationId)) {
          originalMessage.operations = [...originalMessage.operations, operationId];
        }
        originalMessage.status = StreamingMessageStatus.FAILED;
      }
    }),
  );
};
